import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { useAuth } from '../contexts/AuthContext';
import { EnvelopeSimple, Lock, User, ArrowRight, Eye, EyeSlash, At, ArrowLeft, CheckCircle, ShieldCheck, SoccerBall, Trophy, Lightning } from '@phosphor-icons/react';
import { JwflLogo } from '../components/EwflLogo';
import { API_BASE } from '../lib/api';

const API = API_BASE;

const formatError = (detail) => {
  if (!detail) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (typeof detail === 'object' && detail.message) return detail.message;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(' ');
  return String(detail);
};

export const LoginPage = () => {
  const { login, register, refreshUser } = useAuth();
  const [view, setView] = useState('signin');
  const [identifier, setIdentifier] = useState('');
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [name, setName] = useState('');
  const [rememberMe, setRememberMe] = useState(true);
  const [code, setCode] = useState('');
  const [pendingEmail, setPendingEmail] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [resetUid, setResetUid] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tok = params.get('reset_token');
    const uid = params.get('uid');
    if (tok && uid) { setResetToken(tok); setResetUid(uid); setView('reset'); }
  }, []);

  const resetState = () => { setError(''); setInfo(''); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    resetState(); setLoading(true);
    try {
      if (view === 'register') {
        const res = await register(email, password, name, username, rememberMe);
        if (res?.verification_required) {
          setPendingEmail(email);
          // Always require manual entry — never pre-fill the code.
          setCode('');
          if (res.email_sent) {
            setInfo(`We sent a 6-digit code to ${email}. Check your inbox (and spam) — it should arrive within a minute.`);
          } else {
            setInfo(`Account created, but we couldn't send the email right now. Tap "Resend" below to try again.`);
          }
          setView('verify');
        }
      } else if (view === 'signin') {
        await login(identifier, password, rememberMe);
      } else if (view === 'verify') {
        const { data } = await axios.post(`${API}/api/auth/verify-email`,
          { email: pendingEmail, code: code.trim() }, { withCredentials: true });
        if (data?.status === 'verified' || data?.status === 'already_verified') await refreshUser?.();
      } else if (view === 'forgot') {
        await axios.post(`${API}/api/auth/forgot-password`, { email }, { withCredentials: true });
        setView('forgot-sent');
        setInfo(`If ${email} is registered, a password reset link has been sent.`);
      } else if (view === 'reset') {
        const { data } = await axios.post(`${API}/api/auth/reset-password?uid=${encodeURIComponent(resetUid)}`,
          { token: resetToken, password }, { withCredentials: true });
        if (data?.status === 'ok') {
          window.history.replaceState({}, '', window.location.pathname);
          await refreshUser?.();
        }
      }
    } catch (err) {
      // Network failures (CORS, DNS, no internet, server unreachable) leave
      // `err.response` undefined. Surface a clearer message for those so the
      // user can act, instead of the generic "Something went wrong".
      if (!err.response) {
        setError(`Couldn't reach the server. Check your connection and try again.`);
        setLoading(false);
        return;
      }
      const detail = err.response?.data?.detail;
      if (detail?.code === 'EMAIL_NOT_VERIFIED') {
        setPendingEmail(detail.email || email || identifier);
        setCode('');
        setInfo(detail.message || 'Please verify your email to continue.');
        setView('verify');
      } else {
        setError(formatError(detail) || err.message);
      }
    } finally { setLoading(false); }
  };

  const resendCode = async () => {
    resetState(); setLoading(true);
    try {
      await axios.post(`${API}/api/auth/resend-code`, { email: pendingEmail }, { withCredentials: true });
      setInfo(`A fresh code has been sent to ${pendingEmail}.`);
    } catch { setError(`We couldn't send a new code just now.`); }
    finally { setLoading(false); }
  };

  const title =
    view === 'register'    ? 'CREATE ACCOUNT' :
    view === 'verify'      ? 'VERIFY EMAIL'   :
    view === 'forgot'      ? 'RECOVER PASSWORD' :
    view === 'forgot-sent' ? 'CHECK YOUR EMAIL' :
    view === 'reset'       ? 'NEW PASSWORD'  :
                             'WELCOME BACK';

  const subtitle =
    view === 'register'    ? 'Build your career — manager, road to glory, or player.' :
    view === 'verify'      ? `Enter the 6-digit code we sent to ${pendingEmail}.` :
    view === 'forgot'      ? `We'll email a secure link to reset your password.` :
    view === 'forgot-sent' ? 'If the address is registered, a reset link is on the way.' :
    view === 'reset'       ? 'Choose a strong password for your JWFL account.' :
                             'Sign in to continue your season.';

  const inputClass = "pl-11 pr-3 h-12 rounded-md bg-[#070A1A]/90 border border-[#1E2240] text-white text-[14px] placeholder:text-[#5A6390] font-medium focus:border-[#00E5FF] focus:ring-1 focus:ring-[#00E5FF]/40 transition-all duration-150";

  const fieldWrap = (icon, node) => (
    <div className="relative">
      <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#5A6390] pointer-events-none">{icon}</div>
      {node}
    </div>
  );

  return (
    <div className="min-h-screen bg-[#04060F] text-white relative overflow-hidden font-[Manrope]">
      {/* Cinematic neo-Tokyo background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-[#04060F] via-[#070A1A] to-[#04060F]" />
        <div className="absolute -top-[20%] -left-[15%] w-[60vw] h-[60vw] rounded-full opacity-35 blur-3xl"
             style={{ background: 'radial-gradient(circle, rgba(0,229,255,0.50), transparent 60%)' }} />
        <div className="absolute -bottom-[20%] -right-[10%] w-[55vw] h-[55vw] rounded-full opacity-30 blur-3xl"
             style={{ background: 'radial-gradient(circle, rgba(255,0,110,0.45), transparent 60%)' }} />
        <div className="absolute top-[35%] right-[20%] w-[35vw] h-[35vw] rounded-full opacity-20 blur-3xl"
             style={{ background: 'radial-gradient(circle, rgba(204,255,0,0.35), transparent 60%)' }} />
        <svg className="absolute inset-0 w-full h-full opacity-[0.07]" viewBox="0 0 100 100" preserveAspectRatio="none">
          <defs>
            <pattern id="grid" width="6" height="6" patternUnits="userSpaceOnUse">
              <path d="M 6 0 L 0 0 0 6" fill="none" stroke="#00E5FF" strokeWidth="0.06" />
            </pattern>
          </defs>
          <rect width="100" height="100" fill="url(#grid)" />
        </svg>
      </div>

      <div className="relative z-10 min-h-screen flex">
        {/* LEFT — Cinematic brand panel */}
        <div className="hidden lg:flex flex-1 relative items-center justify-center p-12 overflow-hidden">
          <div className="absolute top-12 left-12 flex items-center gap-3">
            <JwflLogo size={48} />
            <div>
              <p className="font-heading font-black text-2xl tracking-wide leading-none">JWFL</p>
              <p className="text-[10px] text-[#00E5FF] tracking-[0.32em] uppercase mt-0.5">Japan World Football League</p>
            </div>
          </div>

          <div className="relative max-w-xl">
            <div className="absolute -top-12 -left-8 w-1 h-32 bg-gradient-to-b from-[#00E5FF] to-transparent" />
            <p className="text-[#00E5FF] tracking-[0.4em] uppercase text-[11px] font-bold mb-4"
              style={{ textShadow: '0 0 10px rgba(0,229,255,0.5)' }}>SEASON 2026 / 27 · 日本</p>
            <h1 className="font-heading font-black uppercase leading-[0.85] tracking-wide text-[8.5rem]">
              <span className="block text-white">FORGE</span>
              <span className="block ewfl-grad-text">YOUR</span>
              <span className="block text-white">DYNASTY.</span>
            </h1>
            <p className="text-[#7A86B5] text-base leading-relaxed mt-6 max-w-md">
              20 J.League clubs. 5 cups. Three modes — Manager, Road to Glory, Player Career — woven into a single neo-Tokyo football world.
            </p>

            <div className="grid grid-cols-3 gap-3 mt-10 max-w-lg">
              {[
                { icon: SoccerBall, label: 'LIVE SIM', sub: 'Broadcast pitch', color: '#00E5FF' },
                { icon: Trophy,     label: '5 CUPS', sub: 'Asia + Domestic',  color: '#FF006E' },
                { icon: Lightning,  label: 'CAREER', sub: 'Player + Manager',color: '#CCFF00' },
              ].map((f, i) => (
                <div key={i} className="bg-[#0A0D1F]/80 backdrop-blur border border-[#1E2240] rounded-md p-3.5 hover:border-[#00E5FF]/60 transition-all"
                  style={{ transition: 'all 0.2s ease' }}>
                  <f.icon size={20} style={{ color: f.color }} weight="fill" />
                  <p className="font-heading font-bold text-sm mt-2 tracking-wider">{f.label}</p>
                  <p className="text-[10px] text-[#7A86B5] mt-0.5">{f.sub}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="absolute bottom-10 left-12 right-12 flex items-center gap-6 text-[10px] text-[#5A6390] tracking-[0.2em] uppercase">
            <span className="flex items-center gap-2">
              <span className="w-2 h-2 bg-[#00FFA3] rounded-full animate-pulse" /> Servers Live
            </span>
            <span>v3.0 · NEO-TOKYO</span>
            <span className="flex-1 h-px bg-gradient-to-r from-[#1E2240] via-[#1E2240] to-transparent" />
          </div>
        </div>

        {/* RIGHT — Auth form panel */}
        <div className="flex-1 flex items-center justify-center p-6 lg:p-12 lg:max-w-[560px] lg:border-l lg:border-[#11142E] relative">
          {/* Mobile brand */}
          <div className="lg:hidden absolute top-6 left-1/2 -translate-x-1/2 flex items-center gap-2">
            <JwflLogo size={32} />
            <p className="font-heading font-black text-xl tracking-wide">JWFL</p>
          </div>

          <div className="w-full max-w-[420px]">
            {/* Form header */}
            <div className="mb-7">
              <p className="text-[#00E5FF] tracking-[0.32em] uppercase text-[10px] font-bold mb-2.5"
                style={{ textShadow: '0 0 8px rgba(0,229,255,0.45)' }}>
                {view === 'signin' && 'AUTHENTICATE'}
                {view === 'register' && 'NEW MANAGER'}
                {view === 'verify' && 'SECURITY CHECK'}
                {view === 'forgot' && 'PASSWORD RESET'}
                {view === 'forgot-sent' && 'SENT'}
                {view === 'reset' && 'RESET'}
              </p>
              <h2 className="font-heading font-black text-white text-[42px] leading-[0.95] tracking-wide">{title}</h2>
              <p className="text-[#7A86B5] text-[13px] mt-3 leading-relaxed">{subtitle}</p>
            </div>

            <form
              onSubmit={handleSubmit}
              className="space-y-3"
              method="post"
              action={view === 'register' ? '/api/auth/register' : '/api/auth/login'}
            >
              {view === 'register' && (
                <>
                  {fieldWrap(<User size={17} />, (
                    <Input data-testid="auth-name" name="name" autoComplete="name"
                      placeholder="Display name" value={name} onChange={(e) => setName(e.target.value)}
                      required className={inputClass} />
                  ))}
                  {fieldWrap(<At size={17} />, (
                    <Input data-testid="auth-username" name="username" autoComplete="username"
                      placeholder="Username (used to sign in)" value={username} onChange={(e) => setUsername(e.target.value)}
                      required minLength={3} className={inputClass} />
                  ))}
                  {fieldWrap(<EnvelopeSimple size={17} />, (
                    <Input data-testid="auth-email" name="email" type="email" autoComplete="email"
                      placeholder="Email address" value={email} onChange={(e) => setEmail(e.target.value)}
                      required className={inputClass} />
                  ))}
                </>
              )}

              {view === 'signin' && fieldWrap(<User size={17} />, (
                <Input data-testid="auth-identifier" name="username" autoComplete="username"
                  placeholder="Email or username" value={identifier} onChange={(e) => setIdentifier(e.target.value)}
                  required className={inputClass} />
              ))}

              {view === 'forgot' && fieldWrap(<EnvelopeSimple size={17} />, (
                <Input data-testid="forgot-email" type="email" autoComplete="email"
                  placeholder="Your email address" value={email} onChange={(e) => setEmail(e.target.value)}
                  required className={inputClass} />
              ))}

              {view === 'verify' && (
                <div className="space-y-3">
                  <Input data-testid="verify-code" name="otp" autoComplete="one-time-code"
                    inputMode="numeric" pattern="[0-9]{6}" maxLength={6}
                    placeholder="• • • • • •" value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                    required
                    className="text-center font-[JetBrains_Mono] text-[34px] font-bold tracking-[0.55em] bg-[#070A1A] border border-[#1E2240] text-white placeholder:text-[#1F2547] h-[68px] rounded-md focus:border-[#00E5FF] focus:ring-1 focus:ring-[#00E5FF]/50" />
                  <p className="text-[11px] text-[#5A6390] text-center">Code expires in 15 minutes. Check your spam folder if it doesn't arrive.</p>
                </div>
              )}

              {(view === 'signin' || view === 'register' || view === 'reset') && (
                <div className="relative">
                  <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#5B7193] pointer-events-none">
                    <Lock size={17} />
                  </div>
                  <Input data-testid="auth-password" name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete={view === 'register' || view === 'reset' ? 'new-password' : 'current-password'}
                    placeholder={view === 'reset' ? 'New password' : 'Password'}
                    value={password} onChange={(e) => setPassword(e.target.value)}
                    required minLength={4}
                    className={`${inputClass} pr-10`} />
                  <button type="button" onClick={() => setShowPassword(v => !v)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    data-testid="toggle-password-visibility"
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 text-[#5B7193] hover:text-white transition-colors">
                    {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              )}

              {(view === 'signin' || view === 'register') && (
                <label className="flex items-center gap-2.5 text-[12.5px] text-[#7A86B5] cursor-pointer select-none pt-1.5" data-testid="remember-me-label">
                  <Checkbox data-testid="remember-me-checkbox" checked={rememberMe}
                    onCheckedChange={(v) => setRememberMe(!!v)}
                    className="border-[#1E2240] data-[state=checked]:bg-[#00E5FF] data-[state=checked]:border-[#00E5FF] data-[state=checked]:text-black" />
                  <span>Keep me signed in for 30 days</span>
                </label>
              )}

              {info && (
                <div className="bg-[#00E5FF]/10 border border-[#00E5FF]/40 rounded-md px-3.5 py-2.5 text-[12px] text-[#66F2FF] leading-relaxed" data-testid="auth-info">{info}</div>
              )}
              {error && (
                <div className="bg-[#FF006E]/12 border border-[#FF006E]/60 rounded-md px-3.5 py-2.5 text-[12.5px] text-[#FF66A8] leading-relaxed" data-testid="auth-error">{error}</div>
              )}

              {view !== 'forgot-sent' && (
                <Button data-testid="auth-submit" type="submit" disabled={loading}
                  className="w-full h-12 mt-1 text-black font-heading font-black uppercase tracking-[0.18em] text-[15px] rounded-md transition-all duration-200 disabled:opacity-60"
                  style={{
                    background: 'linear-gradient(90deg, #00E5FF 0%, #66F2FF 100%)',
                    boxShadow: '0 0 28px rgba(0,229,255,0.50), 0 8px 24px rgba(0,229,255,0.20)',
                  }}>
                  {loading ? 'Please wait…' :
                    view === 'register' ? 'Create account' :
                    view === 'verify'   ? 'Verify & sign in' :
                    view === 'forgot'   ? 'Email me a reset link' :
                    view === 'reset'    ? 'Set password & sign in' :
                                          'Sign in'}
                  {!loading && <ArrowRight size={17} className="ml-2" weight="bold" />}
                </Button>
              )}

              {view === 'forgot-sent' && (
                <div className="text-center py-10">
                  <CheckCircle size={56} className="text-[#00FFA3] mx-auto mb-3" weight="fill" />
                  <p className="text-[14px] text-[#7A86B5]">Check your inbox for the reset link.</p>
                </div>
              )}
            </form>

            {/* Footer links */}
            <div className="mt-6 space-y-3">
              {view === 'signin' && (
                <>
                  <button onClick={() => { resetState(); setView('forgot'); setEmail(''); }}
                    className="text-[12.5px] text-[#7A86B5] hover:text-white transition-colors block w-full text-center"
                    data-testid="forgot-password-link">
                    Forgot your password?
                  </button>
                  <div className="pt-3 border-t border-[#11142E] text-center">
                    <button onClick={() => { resetState(); setView('register'); }}
                      className="text-[13px] text-[#7A86B5] hover:text-white transition-colors"
                      data-testid="auth-toggle">
                      New to JWFL? <span className="text-[#00E5FF] font-heading font-bold tracking-wider uppercase ml-1">Create an account</span>
                    </button>
                  </div>
                </>
              )}
              {view === 'register' && (
                <div className="pt-3 border-t border-[#11142E] text-center">
                  <button onClick={() => { resetState(); setView('signin'); }}
                    className="text-[13px] text-[#7A86B5] hover:text-white transition-colors"
                    data-testid="auth-toggle">
                    Already have an account? <span className="text-white font-heading font-bold uppercase tracking-wider ml-1">Sign in</span>
                  </button>
                </div>
              )}
              {view === 'verify' && (
                <div className="flex items-center justify-between gap-3 pt-3 border-t border-[#11142E]">
                  <button type="button" onClick={() => { resetState(); setView('signin'); setCode(''); }}
                    className="text-[12px] text-[#7A86B5] hover:text-white transition-colors inline-flex items-center gap-1"
                    data-testid="verify-back">
                    <ArrowLeft size={13} /> Back
                  </button>
                  <button type="button" onClick={resendCode} disabled={loading || !pendingEmail}
                    className="text-[12px] text-[#7A86B5] hover:text-white transition-colors disabled:opacity-50"
                    data-testid="resend-code-btn">
                    Didn't get it? <span className="text-[#00E5FF] font-bold ml-1">Resend</span>
                  </button>
                </div>
              )}
              {(view === 'forgot' || view === 'forgot-sent') && (
                <div className="pt-3 border-t border-[#11142E] text-center">
                  <button onClick={() => { resetState(); setView('signin'); }}
                    className="text-[13px] text-[#7A86B5] hover:text-white transition-colors inline-flex items-center gap-1"
                    data-testid="back-signin">
                    <ArrowLeft size={13} /> Back to sign in
                  </button>
                </div>
              )}
            </div>

            <div className="mt-8 pt-5 border-t border-[#11142E] flex items-center justify-center gap-2 text-[10px] text-[#5A6390] tracking-[0.18em] uppercase">
              <ShieldCheck size={13} weight="regular" />
              <span>Secured with email verification</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
